import React from 'react';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { PlainButton } from '../../components/pageStyles';
import { wz, wzType } from '../wizardStyles';

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  opacity: 0;
`;

const Choice = styled.label<{ $disabled?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  > * + * {
    margin-left: 10px;
  }

  input:focus-visible + span {
    outline: 2px solid ${wz.accent};
    outline-offset: 2px;
  }
`;

// 6778:18806 `dot`: 19 round, a 9 dot when on.
const Dot = styled.span<{ $on: boolean }>`
  position: relative;
  flex: none;
  width: 19px;
  height: 19px;
  border: ${wz.line} solid ${({ $on }) => ($on ? wz.accent : wz.muted)};
  border-radius: 50%;

  ${({ $on }) =>
    $on &&
    css`
      &::after {
        position: absolute;
        top: 4.2px;
        left: 4.2px;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: ${wz.accent};
        content: '';
      }
    `}
`;

// 6778:18437 `box`: 19 at r5 (16 at r4 inside a chip), filled with a dark tick when on.
const Box = styled.span<{ $on: boolean; $size: number }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: ${wz.line} solid ${({ $on }) => ($on ? wz.accent : wz.muted)};
  border-radius: ${({ $size }) => ($size > 16 ? 5 : 4)}px;
  background: ${({ $on }) => ($on ? wz.accent : 'transparent')};

  svg {
    width: ${({ $size }) => ($size > 16 ? 12 : 10)}px;
    height: ${({ $size }) => ($size > 16 ? 12 : 10)}px;
  }
`;

const ChoiceLabel = styled.span<{ $on: boolean }>`
  color: ${({ $on }) => ($on ? wz.text : wz.secondary)};
  ${wzType.body}
`;

const Tick: React.FC = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2 5 8.6l4.5-5" stroke={wz.page} strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

interface IRadioGroupProps<T extends string> {
  name: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label?: string;
}

export const RadioGroup = <T extends string>({
  name,
  value,
  options,
  onChange,
  label
}: IRadioGroupProps<T>) => (
  <RadioRow role="radiogroup" aria-label={label}>
    {options.map(option => {
      const on = option.value === value;
      return (
        <Choice key={option.value}>
          <HiddenInput
            type="radio"
            name={name}
            value={option.value}
            checked={on}
            onChange={() => onChange(option.value)}
          />
          <Dot $on={on} aria-hidden="true" />
          <ChoiceLabel $on={on}>{option.label}</ChoiceLabel>
        </Choice>
      );
    })}
  </RadioRow>
);

const RadioRow = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;

  > * + * {
    margin-left: 20px;
  }
`;

interface ICheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Draws the label; a checkbox leading a sentence names itself through `label` only. */
  showLabel?: boolean;
  disabled?: boolean;
}

export const Checkbox: React.FC<ICheckboxProps> = ({
  checked,
  onChange,
  label,
  showLabel = false,
  disabled = false
}) => (
  <Choice $disabled={disabled}>
    <HiddenInput
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={showLabel ? undefined : label}
      onChange={event => onChange(event.target.checked)}
    />
    <Box $on={checked} $size={19} aria-hidden="true">
      {checked ? <Tick /> : null}
    </Box>
    {showLabel ? <ChoiceLabel $on={checked}>{label}</ChoiceLabel> : null}
  </Choice>
);

// 6778:18466 `cb`: a pill of 31.6 around a 16 box and its label, 9 apart.
const Chip = styled.label<{ $disabled: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 31.6px;
  margin: 0 9px 9px 0;
  padding: 7.8px 14.8px 7.8px 11.8px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 999px;
  background: ${wz.field};
  color: ${wz.secondary};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  ${wzType.control}

  > * + * {
    margin-left: 10px;
  }

  input:focus-visible + span {
    outline: 2px solid ${wz.accent};
    outline-offset: 2px;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -9px;
`;

interface IChipGroupProps {
  label: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

export const ChipGroup: React.FC<IChipGroupProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false
}) => (
  <Chips role="group" aria-label={label}>
    {options.map(option => {
      const on = value.includes(option.value);
      return (
        <Chip key={option.value} $disabled={disabled}>
          <HiddenInput
            type="checkbox"
            checked={on}
            disabled={disabled}
            onChange={() =>
              onChange(on ? value.filter(item => item !== option.value) : [...value, option.value])
            }
          />
          <Box $on={on} $size={16} aria-hidden="true">
            {on ? <Tick /> : null}
          </Box>
          <span>{option.label}</span>
        </Chip>
      );
    })}
  </Chips>
);

// 6778:18588 `addprop`: dashed 4 / 4 around a blue plus and a Bold 13 label.
export const DashedAdd = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  min-height: 35.6px;
  padding: 10px 16px;
  border: ${wz.line} dashed ${wz.border};
  border-radius: 8px;
  color: ${wz.accent};
  font-weight: 700;
  ${wzType.label}

  > * + * {
    margin-left: 7px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const AddButton: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ label, onClick, disabled = false }) => (
  <DashedAdd type="button" onClick={onClick} disabled={disabled}>
    <PlusOutlined aria-hidden="true" />
    <span>{label}</span>
  </DashedAdd>
);

// 6778:19828 `selbtn`: a 16 glyph and a Semi Bold 14 label on the field fill.
const Picker = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  min-height: 36.6px;
  padding: 10px 17px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 8px;
  background: ${wz.field};
  color: ${wz.text};
  font-weight: 600;
  ${wzType.input}

  > * + * {
    margin-left: 8px;
  }
`;

export const PickerButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  testId?: string;
}> = ({ icon, label, onClick, testId }) => (
  <Picker type="button" onClick={onClick} data-testid={testId}>
    <span aria-hidden="true">{icon}</span>
    <span>{label}</span>
  </Picker>
);

const Remove = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: ${wz.muted};

  &:hover {
    color: ${wz.text};
  }
`;

export const RemoveButton: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick
}) => (
  <Remove type="button" aria-label={label} onClick={onClick}>
    <CloseOutlined aria-hidden="true" />
  </Remove>
);
